import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import {
  CreatePartnerSnippetRequest,
  PartnerSnippet,
  PartnerSnippetsResponse,
  UpdatePartnerSnippetRequest,
  UpdatePartnerSnippetStatusRequest,
} from './models';

const partnerSnippets = `partner_snippets`;

export const partnerSnippetsApi = createApi({
  reducerPath: partnerSnippets,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partnerSnippets],
  endpoints: (build) => ({
    getPartnerSnippets: build.query<PartnerSnippetsResponse, void>({
      query: () => ({
        url: '/partner/snippets',
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: [{ type: partnerSnippets, id: 'LIST' }],
    }),
    createPartnerSnippet: build.mutation<{ data: PartnerSnippet }, CreatePartnerSnippetRequest>({
      query: (payload) => ({
        url: '/partner/snippets',
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            partnerSnippetsApi.util.updateQueryData('getPartnerSnippets', undefined, (draft) => {
              draft.data.push(response.data);
            }),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    updatePartnerSnippet: build.mutation<{ data: PartnerSnippet }, UpdatePartnerSnippetRequest>({
      query: ({ id, payload }) => ({
        url: `/partner/snippets/${id}`,
        method: 'PATCH',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            partnerSnippetsApi.util.updateQueryData('getPartnerSnippets', undefined, (draft) => {
              const idx = draft.data.findIndex((s) => s.id === id);
              if (idx !== -1) {
                draft.data[idx] = response.data;
              }
            }),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    updatePartnerSnippetStatus: build.mutation<
      { data: PartnerSnippet },
      UpdatePartnerSnippetStatusRequest
    >({
      query: ({ id, statusId, transition }) => ({
        url: `/partner/snippets/${id}/status/${statusId}`,
        method: 'PATCH',
        body: { transition },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            partnerSnippetsApi.util.updateQueryData('getPartnerSnippets', undefined, (draft) => {
              const idx = draft.data.findIndex((s) => s.id === id);
              if (idx !== -1) {
                draft.data[idx] = response.data;
              }
            }),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
  }),
});

export const {
  useGetPartnerSnippetsQuery,
  useCreatePartnerSnippetMutation,
  useUpdatePartnerSnippetMutation,
  useUpdatePartnerSnippetStatusMutation,
} = partnerSnippetsApi;
